import BaseComponent from '@/components/base'
import type { SelectedOption } from '@/types/shopify'

interface VariantPickerOptionSettings {
  onChange?: () => void
}

type SizeLabelMode = 'uk' | 'us'

const classes = {
  unavailable: 'is-unavailable'
}

export default class VariantPickerOption extends BaseComponent {
  static TYPE = 'variant-picker-option'

  settings: VariantPickerOptionSettings
  name: string | undefined
  select: HTMLSelectElement | null
  inputs: HTMLInputElement[]
  sizeLabels: HTMLElement[]
  sizeLabelMode: SizeLabelMode

  constructor(el: HTMLElement, options: VariantPickerOptionSettings = {}) {
    super(el)

    this.settings = {
      ...options
    }

    this.name = this.dataset.name

    if (!this.name) {
      console.warn('No name attribute found')
    }
    
    // Picker options are either <select> tags or a series of <input> tags
    this.select = this.qs('select') as HTMLSelectElement || null
    this.inputs = this.qsa('input') as HTMLInputElement[]
    this.sizeLabels = this.qsa('[data-size-label]')
    this.sizeLabelMode = 'uk'

    this.initializeSizeLabelHover()
    this.setSizeLabelMode(this.sizeLabelMode)

    this.el.addEventListener('change', this.onChange.bind(this))

    // Ensure initial disabled options get the unavailable styling state.
    this.inputs.forEach(input => {
      this.syncInputAvailabilityState(input, !input.disabled)
    })
  }

  syncInputAvailabilityState(input: HTMLInputElement, available: boolean) {
    input.disabled = !available
    input.setAttribute('aria-disabled', String(!available))

    const wrapper = input.closest('.composable-input')

    if (!wrapper) return

    wrapper.classList.toggle(classes.unavailable, !available)
  }

  initializeSizeLabelHover() {
    this.sizeLabels.forEach(label => {
      label.addEventListener('mouseenter', () => {
        this.applyLabelText(label, this.getAlternateSizeLabelMode())
      })

      label.addEventListener('mouseleave', () => {
        this.applyLabelText(label, this.sizeLabelMode)
      })
    })
  }

  getAlternateSizeLabelMode(): SizeLabelMode {
    return this.sizeLabelMode === 'uk' ? 'us' : 'uk'
  }

  applyLabelText(label: HTMLElement, mode: SizeLabelMode) {
    const datasetKey = mode === 'uk' ? 'sizeLabelUk' : 'sizeLabelUs'
    const fallbackKey = mode === 'uk' ? 'sizeLabelUs' : 'sizeLabelUk'
    const labelText = label.dataset[datasetKey] || label.dataset[fallbackKey]

    if (labelText) label.textContent = labelText
  }

  setSizeLabelMode(mode: SizeLabelMode) {
    this.sizeLabelMode = mode

    this.sizeLabels.forEach(label => {
      this.applyLabelText(label, this.sizeLabelMode)
    })
  }

  previewSizeLabelMode(mode?: SizeLabelMode) {
    const targetMode = mode || this.sizeLabelMode

    this.sizeLabels.forEach(label => {
      this.applyLabelText(label, targetMode)
    })
  }

  get selectedOption(): SelectedOption | undefined {
    let name: string | undefined
    let value: string | undefined

    if (this.select) {
      name = this.select.name
      value = this.select.value
    }
    else {
      const selectedInput = this.inputs.find(input => input.checked)

      if (selectedInput) {
        name = selectedInput.name
        value = selectedInput.value
      }
    }

    return name && value ? { name, value } : undefined
  }

  updateValueAvailability(value: string, available: boolean) {
    if (this.select) {
      [...this.select.children].forEach((option: HTMLOptionElement) => {
        if (option.value === value) {
          option.disabled = !available
        }
      })
    }
    else {
      const input = this.inputs.find(input => input.value === value)

      if (!input) return

      this.syncInputAvailabilityState(input, available)
    }
  }

  onChange() {
    this.settings.onChange?.()
  }
}